import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/AuthContext";
import { supabase } from "../../lib/supabase";
import BaseCard from '../common/layout/BaseCard';
import { Button } from '../common/ui/Button';
import Pill from "../common/display/Pill";
import Avatar from "../common/display/Avatar"; // Import the Avatar component
import UserBadges from "./UserBadges"; // Import the UserBadges component
import { captureAppException, createSupabaseError } from '../../utils/errorCaptureUtils'; // Added import
import { NotFound } from "../common/layout/NotFound";

interface UserProfile {
  id: string;
  first_name: string;
  last_name: string;
  organization?: string;
  job_title: string;
  city?: string;
  profile_image_url?: string | null | undefined;
  email: string;
  phone?: string;
  about?: string;
  social_links?: {
    facebook?: string;
    twitter?: string;
    linkedin?: string;
  };
  country_id?: string;
  industry_id?: string;
  job_function_id?: string;
}

interface UserTaxonomies {
  sectors: string[];
  subsectors: string[];
  platforms: string[];
  tools: string[];
  programming_languages: string[];
}

interface TaxonomyData {
  sectors: Record<string, string>;
  subsectors: Record<string, string>;
  platforms: Record<string, string>;
  tools: Record<string, string>;
  programmingLanguages: Record<string, string>;
  countries: Record<string, string>;
  industries: Record<string, string>;
  jobFunctions: Record<string, string>;
}

interface UserWorkspace {
  workspace_id: string;
  workspace_name: string;
  workspace_type: 'personal' | 'team';
  role: 'owner' | 'editor' | 'member';
  logo_url?: string | null;
}

interface UserData extends UserProfile {
  user_sectors: Array<{ sector_id: string }>;
  user_subsectors: Array<{ subsector_id: string }>;
  user_platforms: Array<{ platform_id: string }>;
  user_tools: Array<{ tool_id: string }>;
  user_programming_languages: Array<{ programming_language_id: string }>;
}

const defaultProfile: UserProfile = {
  id: "",
  first_name: "",
  last_name: "",
  email: "",
  job_title: "",
  social_links: {},
};

export const ProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile>(defaultProfile);
  const [userTaxonomies, setUserTaxonomies] = useState<UserTaxonomies>({
    sectors: [],
    subsectors: [],
    platforms: [],
    tools: [],
    programming_languages: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [taxonomyData, setTaxonomyData] = useState<TaxonomyData>({
    sectors: {},
    subsectors: {},
    platforms: {},
    tools: {},
    programmingLanguages: {},
    countries: {},
    industries: {},
    jobFunctions: {},
  });
  const [workspaces, setWorkspaces] = useState<UserWorkspace[]>([]);

  const isOwnProfile = !id || id === user?.id;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          { data: sectors },
          { data: subsectors },
          { data: platforms },
          { data: tools },
          { data: programmingLanguages },
          { data: countries },
          { data: industries },
          { data: jobFunctions },
          { data: workspaceData, error: workspaceError }
        ] = await Promise.all([
          supabase.from("sectors").select("id, name"),
          supabase.from("subsectors").select("id, name"),
          supabase.from("platforms").select("id, name"),
          supabase.from("tools").select("id, name"),
          supabase.from("programming_languages").select("id, name"),
          supabase.from("countries").select("id, name"),
          supabase.from("industries").select("id, name"),
          supabase.from("job_functions").select("id, name"),
          supabase
            .from('workspace_users')
            .select(`
              workspace_id,
              role,
              workspaces (
                workspace_name,
                workspace_type,
                logo_url
              )
            `)
            .eq('user_id', id || user?.id)
        ]);

        if (workspaceError) throw createSupabaseError(workspaceError, 'fetch_workspace');

        const transformedWorkspaces = (workspaceData || []).map((wu: any) => ({
          workspace_id: wu.workspace_id,
          workspace_name: wu.workspaces.workspace_name,
          workspace_type: wu.workspaces.workspace_type,
          logo_url: wu.workspaces.logo_url,
          role: wu.role,
        }));

        setWorkspaces(transformedWorkspaces);

        const createLookup = (data: any[] | null) =>
          Object.fromEntries((data || []).map((item) => [item.id, item.name]));

        setTaxonomyData({
          sectors: createLookup(sectors),
          subsectors: createLookup(subsectors),
          platforms: createLookup(platforms),
          tools: createLookup(tools),
          programmingLanguages: createLookup(programmingLanguages),
          countries: createLookup(countries),
          industries: createLookup(industries),
          jobFunctions: createLookup(jobFunctions),
        });

        await fetchProfile();
      } catch (err) {
        console.error("Error fetching profile data:", err);
        captureAppException(err, {
          tags: {
            context: 'profile_page',
            action: 'fetch_profile_data',
            profile_user_id: id || user?.id
          }
        });
        setError("Failed to load profile data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, user?.id]);

  const fetchProfile = async () => {
    try {
      // Only select necessary fields, excluding sensitive information for other users
      const defaultFields = `
        id,
        first_name,
        last_name,
        organization,
        job_title,
        city,
        profile_image_url,
        about,
        social_links,
        country_id,
        industry_id,
        job_function_id,
        user_sectors(sector_id),
        user_subsectors(subsector_id),
        user_platforms(platform_id),
        user_tools(tool_id),
        user_programming_languages(programming_language_id)
      `;

      const ownFields = `email, phone, ${defaultFields}`
      const selectionQuery = isOwnProfile ? ownFields : defaultFields

      // @ts-ignore
      const { data, error } = await supabase
        .from('users')
        .select(selectionQuery)
        .eq("id", id || user?.id)
        .single() as { data: UserData | null; error: any };

      if (error) throw error;
      if (!data) throw new Error("No data found");

      // Extract taxonomy relationships
      const taxonomies: UserTaxonomies = {
        sectors: data.user_sectors.map(s => s.sector_id),
        subsectors: data.user_subsectors.map(s => s.subsector_id),
        platforms: data.user_platforms.map(p => p.platform_id),
        tools: data.user_tools.map(t => t.tool_id),
        programming_languages: data.user_programming_languages.map(l => l.programming_language_id)
      };

      setUserTaxonomies(taxonomies);

      setProfile({
        ...defaultProfile,
        ...data,
      });
    } catch (err) {
      console.error("Error fetching profile data:", err);
      captureAppException(err, {
        tags: {
          context: 'profile_page',
          action: 'fetch_profile_data',
          profile_user_id: id || user?.id
        }
      });
      setError("Failed to load profile data.");
    }
  };

  if (error || (!profile && !loading)) {
    return (
      <NotFound 
        title="Profile Not Found"
        message={error || "The profile you're looking for doesn't exist or has been removed."}
      />
    );
  }

  return (
    <div className="space-y-6">
      <BaseCard>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-6">
            <Avatar
              firstName={profile.first_name}
              lastName={profile.last_name || ""}
              profileImageUrl={profile.profile_image_url}
              size="large"
              className="w-24 h-24 flex-shrink-0"
            />
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold text-gray-900 break-words">
                {profile.first_name} {profile.last_name}
              </h1>
              {profile.job_title && (
                <p className="text-gray-600 mt-1 break-words">{profile.job_title}</p>
              )}
              {profile.organization && (
                <p className="text-gray-600 break-words">{profile.organization}</p>
              )}
            </div>
          </div>
          {isOwnProfile && (
            <div className="flex gap-2 self-start md:self-auto">
              <Button
                onClick={() => navigate("/profile/edit")}
                variant="outline"
                size="small"
              >
                Edit Profile
              </Button>
            </div>
          )}
        </div>
      </BaseCard>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          <BaseCard>
            <h2 className="text-lg font-bold text-gray-900 mb-4">Working In</h2>
            {profile.industry_id && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-500">Industry</h3>
                <p className="text-gray-900">
                  {taxonomyData.industries[profile.industry_id]}
                </p>
              </div>
            )}
            {profile.job_function_id && (
              <div>
                <h3 className="text-sm font-medium text-gray-500">Role</h3>
                <p className="text-gray-900">
                  {taxonomyData.jobFunctions[profile.job_function_id]}
                </p>
              </div>
            )}
          </BaseCard>

          <BaseCard>
            <h2 className="text-lg font-bold text-gray-900 mb-4">Interests</h2>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {userTaxonomies.sectors.map((sectorId) => (
                  <Pill
                    key={sectorId}
                    text={taxonomyData.sectors[sectorId]}
                    className="bg-blue-100 text-blue-700 rounded-full text-sm"
                  />
                ))}
                {userTaxonomies.subsectors.map((subsectorId) => (
                  <Pill
                    key={subsectorId}
                    text={taxonomyData.subsectors[subsectorId]}
                    className="bg-blue-50 text-blue-600 rounded-full text-sm"
                  />
                ))}
              </div>
            </div>
          </BaseCard>

          <BaseCard>
            <h2 className="text-lg font-bold text-gray-900 mb-4">Experience</h2>
            <div className="space-y-4">
              {userTaxonomies.platforms.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">
                    Platforms
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {userTaxonomies.platforms.map((platformId) => (
                      <Pill
                        key={platformId.toString()}
                        text={taxonomyData.platforms[platformId]}
                        className="bg-gray-100 text-gray-700"
                      />
                    ))}
                  </div>
                </div>
              )}
              {userTaxonomies.tools.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">
                    Tools
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {userTaxonomies.tools.map((toolId) => (
                      <Pill
                        key={toolId.toString()}
                        text={taxonomyData.tools[toolId]}
                        className="bg-gray-100 text-gray-700"
                      />
                    ))}
                  </div>
                </div>
              )}
              {userTaxonomies.programming_languages.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">
                    Programming Languages
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {userTaxonomies.programming_languages.map((langId) => (
                      <Pill
                        key={langId.toString()}
                        text={taxonomyData.programmingLanguages[langId]}
                        className="bg-gray-100 text-gray-700"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </BaseCard>
        </div>

        {/* Middle Column */}
        <div className="space-y-6">
          <BaseCard>
            <h2 className="text-lg font-bold text-gray-900 mb-4">About</h2>
            {profile.about ? (
              <p className="text-gray-600 whitespace-pre-wrap mb-4">
                {profile.about}
              </p>
            ) : (
              <p className="text-gray-500 italic mb-4">No bio added yet</p>
            )}
            <div className="space-y-2">
              {profile.job_title && (
                <div className="flex items-center gap-2 text-gray-600">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    {/* Icon Path */}
                  </svg>
                  {profile.job_title}
                </div>
              )}
              {profile.organization && (
                <div className="flex items-center gap-2 text-gray-600">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    {/* Icon Path */}
                  </svg>
                  {profile.organization}
                </div>
              )}
              {(profile.city || profile.country_id) && (
                <div className="flex items-center gap-2 text-gray-600">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    {/* Icon Path */}
                  </svg>
                  {[
                    profile.city,
                    profile.country_id
                      ? taxonomyData.countries[profile.country_id]
                      : null,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </div>
              )}
              {workspaces.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Teams</h3>
                  <div className="space-y-2">
                    {workspaces
                      .filter(w => w.workspace_type === 'team')
                      .map(workspace => (
                        <div
                          key={workspace.workspace_id}
                          className="flex items-center gap-2 text-gray-600 hover:bg-gray-50 rounded-md p-1 cursor-pointer"
                          onClick={() => navigate(`/workspace/${workspace.workspace_id}`)}
                        >
                          {workspace.logo_url ? (
                            <img
                              src={workspace.logo_url}
                              alt={`${workspace.workspace_name} logo`}
                              className="w-6 h-6 rounded-md object-cover"
                            />
                          ) : (
                            <div className="w-6 h-6 rounded-md bg-gray-100 flex items-center justify-center text-gray-500 text-sm font-medium">
                              {workspace.workspace_name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <span>
                            {workspace.workspace_name}
                            <span className="text-gray-400 text-sm ml-1">
                              ({workspace.role})
                            </span>
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </BaseCard>

          {isOwnProfile && profile.email && (
            <BaseCard>
              <h2 className="text-lg font-bold text-gray-900 mb-4">Contact</h2>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-gray-600">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    {/* Icon Path */}
                  </svg>
                  {profile.email}
                </div>
                {profile.phone && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      {/* Icon Path */}
                    </svg>
                    {profile.phone}
                  </div>
                )}
              </div>
            </BaseCard>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          <UserBadges userId={(id || user?.id) ?? ""} />
          
          <BaseCard>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-900">Communities</h2>
              <Button
                onClick={() => navigate("/communities")}
                variant="ghost"
                size="small"
              >
                View All
              </Button>
            </div>
            <div className="space-y-4">
              <p className="text-gray-500 italic">No communities yet</p>
            </div>
          </BaseCard>
        </div>
      </div>
    </div>
  );
};